import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { catchError, map, Observable, of, tap, throwError, first } from 'rxjs';
import { GungBase } from '../../models/base';
import { S3Image } from '../../models/s3';
import { GungDownloadUrl, GungImageUrlService } from 'gung-common';
import { NavigationConfig } from '../../models/navigation-config';

export interface GungCompany extends GungBase {
  address1: string;
  address2: string;
  zipCode: string;
  city: string;
  country: string;
  organizationNumber: string;
  email: string;
  telephone: string;
  webAddress: string;
  logo: S3Image;
  loginBackgroundImage: S3Image;
  logoLogin: S3Image;
  logoOrderConfirm?: S3Image;
  favicon?: S3Image;
  colors: { [s: string]: string } | null;
}

@Injectable({
  providedIn: 'root'
})
export class GungCompanyService {
  protected serviceUrl = `json/gung-company`;
  protected serviceUrlWithCompany = `json/gung-company`;
  private companyCached: GungCompany;

  protected myCompany: string[] = [];

  constructor(
    protected http: HttpClient,
    protected gungImageUrlService: GungImageUrlService,
    @Optional()
    @Inject('environment')
    protected environment: NavigationConfig
  ) {
    this.checkEnvToSetCompanyId();
  }

  getGungCompany(): Observable<GungCompany> {
    return this.http.get<GungCompany>(this.serviceUrlWithCompany);
  }

  postGungCompany(gungCompany: GungCompany): Observable<GungCompany> {
    return this.http.post<GungCompany>(this.serviceUrl, gungCompany);
  }

  getPublicGungCompany(nocache?: boolean): Observable<GungCompany> {
    if (!nocache || !this.companyCached) {
      const headers = {
        maxAge: nocache ? '-1' : '60'
      };
      return this.http
        .get<GungCompany>(`public/${this.serviceUrlWithCompany}`, { headers })
        .pipe(/* catchError(err => of({} as any)), */ tap(company => (this.companyCached = company)));
    }
    return of(this.companyCached);
  }

  checkEnvToSetCompanyId() {
    if (!!this.environment?.beGungCompany) {
      this.serviceUrlWithCompany += `/${this.environment.beGungCompany}`;
    }
  }

  getColors(noCache?: boolean): Observable<{ [s: string]: string } | null> {
    return this.getPublicGungCompany(noCache).pipe(
      catchError(err => throwError(err)),
      catchError(error => of(null)),
      first(),
      map(company => {
        return company?.colors;
      })
    );
  }

  getExtraCustomization(noCache?: boolean): Observable<{ [s: string]: string } | null> {
    return this.getPublicGungCompany(noCache).pipe(
      catchError(err => throwError(err)),
      catchError(error => of(null)),
      first(),
      map(company => {
        return company?.extra?.customization;
      })
    );
  }

  getExtraFonts(noCache?: boolean): Observable<{ [s: string]: string } | null> {
    return this.getPublicGungCompany(noCache).pipe(
      catchError(err => throwError(err)),
      catchError(error => of(null)),
      first(),
      map(company => {
        if (company) {
          const { WOFF2, WOFF, TTF } = company.extra;
          return { WOFF2: WOFF2?.s3Uri, WOFF: WOFF?.s3Uri, TTF: TTF?.s3Uri };
        }
        return undefined;
      })
    );
  }

  public getLogoUrlObservable(noCache?: boolean): Observable<string> {
    return this.getPublicGungCompany(noCache).pipe(
      catchError(err => throwError(err)),
      catchError(error => of(null)),
      first(),
      map(company => {
        return this.getLogoFromCompany(company);
      })
    );
  }

  private getLogoFromCompany(company: GungCompany): string {
    if (company?.logo?.s3Uri && !company.logo.s3Uri.startsWith('https://')) {
      if (company.logo.s3Uri.endsWith('.gif')) {
        return GungDownloadUrl(company.logo.s3Uri);
      } else {
        return this.gungImageUrlService.getUrl(company.logo.s3Uri);
      }
    }
    return null; // get default
  }

  public getLogoLoginUrlObservable(noCache?: boolean): Observable<string> {
    return this.getPublicGungCompany(noCache).pipe(
      catchError(err => throwError(err)),
      catchError(error => of(null)),
      first(),
      map(company => {
        if (company?.logoLogin?.s3Uri && !company.logoLogin.s3Uri.startsWith('https://')) {
          if (company.logoLogin.s3Uri.endsWith('.gif')) {
            return GungDownloadUrl(company.logoLogin);
          } else {
            return this.gungImageUrlService.getUrl(company.logoLogin.s3Uri);
          }
        } else {
          return this.getLogoFromCompany(company);
        }
      })
    );
  }

  public getBackgroundLoginUrlObservable(noCache?: boolean): Observable<string> {
    return this.getPublicGungCompany(noCache).pipe(
      catchError(err => throwError(err)),
      catchError(error => of(null)),
      first(),
      map(company => {
        if (company?.loginBackgroundImage?.s3Uri && !company.loginBackgroundImage.s3Uri.startsWith('https://')) {
          if (company.loginBackgroundImage.s3Uri.endsWith('.gif')) {
            return GungDownloadUrl(company.loginBackgroundImage);
          } else if (company.loginBackgroundImage.s3Uri.endsWith('.mp4') || company.loginBackgroundImage.s3Uri.endsWith('.webm') || company.loginBackgroundImage.s3Uri.endsWith('.ogg')) {
            return 'https://cdn2.gung.io/' + company.loginBackgroundImage.s3Uri;
          } else {
            return this.gungImageUrlService.getUrl(company.loginBackgroundImage.s3Uri);
          }
        }
        return null; // get default
      })
    );
  }

  public getFaviconUrlObservable(noCache?: boolean): Observable<string> {
    return this.getPublicGungCompany(noCache).pipe(
      catchError(err => throwError(err)),
      catchError(error => of(null)),
      first(),
      map(company => {
        if (company?.favicon?.s3Uri && !company.favicon.s3Uri.startsWith('https://')) {
          return GungDownloadUrl(company.favicon.s3Uri);
        }
        // Keep old logic as fallback
        if (company?.extra?.favicon?.s3Uri && !company.extra.favicon.s3Uri.startsWith('https://')) {
          return GungDownloadUrl(company.extra.favicon.s3Uri);
        }
        return null; // get default
      })
    );
  }
}
